import { IProps as IBasicSelectProps } from "@components/inputs/select/BasicSelect";
import {
    ISelectItem,
    ISelectPropsBase,
    TOnItemsFetchedCallback,
    TSelectItemId
} from "@components/inputs/select/Select.types";
import { getNestedValue, setNestedValue } from "@odata/Data.utils";
import { IFieldInfo } from "@odata/FieldInfo.utils";
import { getEnumDisplayValue, getEnumName, isEnum, loadEnums } from "@odata/GeneratedEnums.utils";
import { formatValue, IFormatOptions } from "@odata/OData.utils";
import { WithOData, withOData } from "@odata/withOData";
import { isDefined, isNotDefined, isObjectEmpty } from "@utils/general";
import { debounce } from "lodash";
import React from "react";

import { INPUT_DEBOUNCE_TIME } from "../../../constants";
import { AppContext } from "../../../contexts/appContext/AppContext.types";
import { TValue } from "../../../global.types";
import { Model } from "../../../model/Model";
import BindingContext, { TEntityKey } from "../../../odata/BindingContext";
import { FormStorage } from "../../../views/formView/FormStorage";
import { IMultiSelectProps } from "../../inputs/select/MultiSelect";
import { buildTreeFromAllItems, getItemsForRenderCallbackArgs } from "../../inputs/select/SelectAPI";
import { ISmartEventData } from "../FieldInfo";
import { fetchItemsByInfo } from "./SmartSelectAPI";

export interface ISmartSelectBaseProps {
    // todo fieldInfo to fieldDef
    fieldInfo: IFieldInfo;
    bindingContext: BindingContext;
    storage?: Model;
    isHierarchical?: boolean;
    onClick?: (e: React.MouseEvent, args: ISmartEventData) => void;
    onIconActivate?: (e: React.MouseEvent, args: ISmartEventData) => void;
    onItemsFetched?: (bc: BindingContext, items: ISelectItem[]) => void;
    getCustomTabularData?: (val: TValue, args: IFormatOptions) => string[];
}

interface IProps extends ISmartSelectBaseProps, WithOData {
    selectProps?: ISelectPropsBase | IMultiSelectProps;
    select: React.ComponentType<any>;
    value?: TSelectItemId | TSelectItemId[];
}


class SmartSelectBase extends React.PureComponent<IProps> {
    static contextType = AppContext;
    //sadly, breaks typescript type checking
    //context: React.ContextType<typeof AppContext>;
    _isMounted: boolean;

    // we need to put this outside of state as we need sync operation with the property
    isLoading: boolean;

    constructor(props: IProps) {
        super(props);

        if (!this.shouldLoadItems()) {
            this.setInitialItems();
        }
    }

    componentDidMount = async () => {
        this._isMounted = true;
        if (this.shouldLoadItems()) {
            this.load();
        } else {
            this.setInitialItems();
        }
    };

    componentDidUpdate(prevProps: Readonly<IProps>, prevState: Readonly<{}>) {
        if (this.shouldLoadItems()) {
            this.load();
        } else {
            this.setInitialItems();
        }
    }

    shouldLoadItems = (): boolean => {
        const { fieldSettings } = this.props.fieldInfo;
        return !!fieldSettings.preloadItems || (!!fieldSettings.itemsFactory && fieldSettings.cacheFetchedItemsInFieldInfo !== false);
    };

    componentWillUnmount(): void {
        this._isMounted = false;
    }

    // fieldSettings.items can be callback, but once it's called, we work directly with the array
    get items(): ISelectItem[] {
        return this.props.fieldInfo.fieldSettings?.items;
    }

    set items(items: ISelectItem[]) {
        this.props.fieldInfo.fieldSettings.items = items;
    }

    getHandleItemsFetchRequested = (): IBasicSelectProps["onItemsFetchRequested"] => {
        const { fieldInfo: info } = this.props;
        if (info.fieldSettings?.onItemsFetchRequested ||
            (isDefined(info.fieldSettings?.items) && info.fieldSettings?.count > info.fieldSettings.items.length)) {
            return this.handleItemsFetchRequested;
        }
        return null;
    };

    load = async (reload?: boolean) => {
        // storage.loading -> do not trigger load before storage is loaded - it would load data according to wrong filters (based on previously loaded entity)
        if (this.isLoading || !this.props.storage || this.props.storage.loading) {
            return;
        }

        if (this.props.fieldInfo.fieldSettings.cacheFetchedItemsInFieldInfo === false && reload === true) {
            // remove items, so they can be fetched again without caching
            this.items = null;
        }

        if (this.props.fieldInfo && !this.items) {
            this.isLoading = true;
            this.forceUpdate();
            await this.fetchItems();
        }

        if (this.props.isHierarchical && this.items?.length > 0) {
            const item = this.items[0];
            // check whether items are already transformed
            if (isNotDefined(item.indent)) {
                this.items = buildTreeFromAllItems(this.items);
            }
        }
        // in case not all values has it's item, preselect also with initial items, so the value is visible anyway
        this.setInitialItems();

        if (this.isLoading) {
            this.isLoading = false;
            this.forceUpdate();
        }
    };

    isEnum = (): boolean => {
        return isEnum(this.props.fieldInfo);
    };

    getEnumName = (): string => {
        return getEnumName(this.props.fieldInfo, this.props.oData);
    };

    setInitialItems = async () => {
        const { bindingContext, storage, value, fieldInfo } = this.props;

        const values = (Array.isArray(value) ? value : [value]).filter(isDefined);
        const hasValue = values?.length > 0;
        if (!hasValue || fieldInfo.fieldSettings.allowCreate) {
            return;
        }

        const currentInitialItems = this.props.fieldInfo.fieldSettings?.initialItems;
        const { items } = this.props.fieldInfo.fieldSettings ?? {};
        const containsCurrentItems = !values?.length || values.every(val => items?.some(item => item.id === val));
        const shouldSetInitialItem = (isObjectEmpty(items) || !containsCurrentItems) && !bindingContext.isLocal();

        const alreadySet = currentInitialItems?.length === values?.length && values.every(val => currentInitialItems.some(item => item.id === val));
        if (!shouldSetInitialItem || alreadySet) {
            return;
        }

        let enumName: string;

        if (this.isEnum()) {
            // we need to load the enum name space even without initial item, to show correct translations in conditional dialog
            enumName = this.getEnumName();

            if (!enumName) {
                return;
            }

            const { namespacesPromise, notLoadedNamespaces } = loadEnums(enumName);

            if (notLoadedNamespaces.length > 0) {
                await namespacesPromise;
                // because we load the name space manually,
                // we have to trigger manual rerender as well
                this.forceUpdate();
            }
        }

        if (!value || values.length === 0) {
            return;
        }

        const allAdditionalProperties = new Set([...(this.props.fieldInfo.additionalProperties ?? []), ...(this.props.fieldInfo.columns ?? [])]?.filter(v => v).map(property => property.id));

        if (this.props.fieldInfo.fieldSettings.displayName) {
            allAdditionalProperties.add(this.props.fieldInfo.fieldSettings.displayName);
        }

        let initialItems: ISelectItem[] = values.map(val => {
            const keyPropName = bindingContext.getKeyPropertyName();
            const itemBc = bindingContext.isCollection() ? bindingContext.addKey(val as TEntityKey) : bindingContext;

            const item: ISelectItem = {
                id: val,
                additionalData: {
                    [keyPropName]: val
                }
            };

            if (this.isEnum()) {
                item.additionalData.Name = getEnumDisplayValue(enumName, val as string);
            }

            for (const prop of allAdditionalProperties) {
                try {
                    // we can't ensure always correct path
                    const bc = itemBc.navigate(prop);
                    const val = storage.getValue(bc);
                    if (val) {
                        item.additionalData[prop] = val;
                    }
                } catch {
                }
            }

            if (this.props.fieldInfo.fieldSettings.displayName && !this.props.fieldInfo.formatter) {
                item.label = item.additionalData[this.props.fieldInfo.fieldSettings.displayName];
            } else if (this.isEnum()) {
                item.label = item.additionalData.Name;
            } else {
                item.label = formatValue(val, this.props.fieldInfo, {
                    entity: item.additionalData,
                    item: item.additionalData,
                    storage: this.props.storage,
                    bindingContext: this.props.bindingContext
                }) ?? val?.toString() ?? "";
            }

            if (this.props.fieldInfo.columns) {
                item.tabularData = this.props.fieldInfo.columns.map(col => {
                    const val = item.additionalData[col.id];

                    if (col.fieldSettings?.displayName && !col.formatter && typeof val === "object") {
                        return val[col.fieldSettings.displayName];
                    }

                    return formatValue(val, {
                        ...col,
                        bindingContext: this.props.bindingContext.isValidNavigation(col.id) ? this.props.bindingContext.navigate(col.id) : this.props.bindingContext,
                    }, {
                        entity: item.additionalData,
                        item: item.additionalData,
                        storage: this.props.storage
                    }) ?? val?.toString() ?? "";
                });
            }

            if (this.props.fieldInfo.fieldSettings.localDependentFields?.length) {
                // transform dependent fields from model to additionalData (reverse transformation)
                for (const field of this.props.fieldInfo.fieldSettings.localDependentFields) {
                    const data = storage.getValue(itemBc);
                    const dependentValue = getNestedValue(field.to?.id ?? field.from.id, data);
                    if (isDefined(dependentValue)) {
                        setNestedValue(dependentValue, field.from.id, item.additionalData);
                    }
                }
            }

            return item;
        });

        if (initialItems?.length) {
            if (!this.props.fieldInfo.fieldSettings) {
                this.props.fieldInfo.fieldSettings = {};
            }

            if (this.props.fieldInfo.fieldSettings.transformFetchedItems) {
                // apply the transformFetchedItems even to initial items or something could be missing from the item,
                // e.g. token link, etc. basically all non-data source related transformations
                initialItems = this.props.fieldInfo.fieldSettings.transformFetchedItems(initialItems,
                    getItemsForRenderCallbackArgs(this.props.storage, this.props.fieldInfo, this.props.bindingContext, this.context));
            }

            this.props.fieldInfo.fieldSettings.initialItems = initialItems;
            this.props.storage.addActiveField(this.props.bindingContext);
            // make the call debounced, to improve performance
            this.props.storage.refreshFields(false, true);
        }
    };

    handleItemsFetchRequested = debounce(async (value: string, onFetched?: TOnItemsFetchedCallback) => {
        const { fieldInfo } = this.props;
        if (fieldInfo.fieldSettings?.onItemsFetchRequested) {
            fieldInfo.fieldSettings?.onItemsFetchRequested(value, (items: ISelectItem[]) => {
                this.items = items;
                onFetched?.(items);
            });
        } else if (value) {
            await this.fetchItems(value);
            onFetched?.(fieldInfo.fieldSettings.items);
        }
    }, INPUT_DEBOUNCE_TIME);

    // todo when there are bigger amount of same SmartSelects used on one page, this function is kidna slow
    // it would be nice to only call it once for all selects with the same definition.
    // at the moment we only cache getItems, but not the rest of it even though some parts of it, like transformItems,
    // are quite performance heavy
    fetchItems = async (value?: string) => {
        const { fieldInfo, storage, isHierarchical, bindingContext } = this.props;

        this.items = await fetchItemsByInfo(storage as FormStorage, fieldInfo, isHierarchical, value);

        if (fieldInfo.fieldSettings !== this.props.fieldInfo.fieldSettings) {
            console.warn(`SmartSelectBase::fetchItems(${bindingContext.getFullPath()}) - items loading was triggered with different fieldInfo.fieldSettings object than it was passed after data resolution. Copying items to new fieldSettings.`);
            if (!this.props.fieldInfo.fieldSettings) {
                this.props.fieldInfo.fieldSettings = {};
            }
            this.props.fieldInfo.fieldSettings.items = fieldInfo.fieldSettings.items;
        }

        this.props.onItemsFetched?.(bindingContext, this.items);

        if (this._isMounted) {
            if (storage) {
                // rerender read only item in the SmartFilterBar for the same bc as well
                storage.addActiveField(this.props.bindingContext);
                // make the call debounced, to improve performance
                this.props.storage.refreshFields(false, true);
            } else {
                this.forceUpdate();
            }
        }
    };

    handleClick = (e: React.MouseEvent) => {
        const { storage, bindingContext } = this.props;
        return this.props.onClick?.(e, { storage, bindingContext });
    };

    handleOpen = (): void => {
        this.load(true);
    };

    handleFocus = (): void => {
        // we don't want user that use keyboard to navigate wait for items to load on the first arrow down click (which seems like a bug because nothing happens)
        // => fetch immediately on focus
        this.load(true);
    };

    handleIconActivate = (e: React.MouseEvent) => {
        const { storage, bindingContext } = this.props;
        return this.props.onIconActivate?.(e, { storage, bindingContext });
    };

    render() {
        const Select = this.props.select;

        const items = this.props.fieldInfo?.fieldSettings?.itemsForRender?.(
            this.items ?? [],
            getItemsForRenderCallbackArgs(this.props.storage, this.props.fieldInfo, this.props.bindingContext, this.context)
        ) || this.items || [];

        // if some value items are missing after the itemsForRender transformation, add them as initialItems
        const { fieldInfo } = this.props;
        const values = (Array.isArray(this.props.value) ? this.props.value : [this.props.value]).filter(isDefined);
        if (values?.length > (fieldInfo?.fieldSettings?.initialItems?.length ?? 0)) {
            const itemsMap = new Map<TSelectItemId, ISelectItem>();
            items.forEach(item => itemsMap.set(item.id, item));
            const missingItemIds = values
                .filter(val => !itemsMap.has(val));
            if (fieldInfo && missingItemIds.length > 0) {
                if (!fieldInfo.fieldSettings?.initialItems) {
                    if (!fieldInfo.fieldSettings) {
                        fieldInfo.fieldSettings = {};
                    }
                    fieldInfo.fieldSettings.initialItems = [];
                }
                missingItemIds.forEach(itemId => {
                    const item = (this.items ?? []).find(i => i.id === itemId);
                    if (item && !fieldInfo.fieldSettings.initialItems.find(i => i.id === item.id)) {
                        fieldInfo.fieldSettings.initialItems.push(item);
                    }
                });
            }
        }

        let customTabularData: string[] = null;
        if (this.props.getCustomTabularData) {
            customTabularData = this.props.getCustomTabularData(this.props.value as TValue, {
                storage: this.props.storage,
                bindingContext: this.props.bindingContext
            });
        }

        // take out properties that are only relevant of SmartSelectBase
        const {
            bindingContext,
            storage,
            isHierarchical,
            onClick,
            onIconActivate,
            oData,
            ...restSelectProps
        } = this.props;

        return (<Select
                filteredCount={fieldInfo?.fieldSettings?.filteredCount}
                {...this.props.selectProps}
                {...restSelectProps}
                onItemsFetchRequested={this.getHandleItemsFetchRequested()}
                onClick={this.handleClick}
                isLoading={this.isLoading}
                onOpen={this.handleOpen}
                onIconActivate={this.handleIconActivate}
                onFocus={this.handleFocus}
                items={items}
                initialItems={this.props.fieldInfo?.fieldSettings?.initialItems}
                columns={this.props.fieldInfo?.columns}
                customTabularData={customTabularData}
            />
        );
    }
}

export default withOData(SmartSelectBase);